import React from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight, MapPin } from "lucide-react";
import { Card, EmptyState } from "../common/Primitives";

/**
 * HotDealsSection
 * ---------------
 * Trending startups by interest count in the last 30 days.
 * Excludes startups the investor already has interest in.
 */
export default function HotDealsSection({ deals }) {
  return (
    <Card testId="section-hot-deals">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline flex items-center gap-1.5 text-rose-600">
            <Flame size={12} /> Hot on VenturaZ
          </div>
          <div className="font-heading text-ink text-lg mt-1">Trending this month</div>
        </div>
        <Link to="/investor/marketplace" className="text-emerald-700 text-xs font-heading hover:underline" data-testid="hot-deals-see-all">
          Marketplace →
        </Link>
      </div>

      {(!deals || deals.length === 0) ? (
        <EmptyState title="No trending deals yet" hint="Check back — investor interest builds fast here." />
      ) : (
        <div className="space-y-2">
          {deals.slice(0, 5).map((d, idx) => (
            <Link
              to={`/startup/${d.startup_id}`}
              key={d.startup_id}
              className="block border border-white/10 p-3 rounded hover:border-rose-300 hover:bg-rose-50/20 transition-colors relative"
              data-testid={`hot-deal-${d.startup_id}`}
            >
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white text-[10px] font-heading flex items-center justify-center flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-heading text-ink text-sm truncate">{d.startup_name}</div>
                    <div
                      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-rose-700 flex-shrink-0"
                      data-testid={`hot-deal-count-${d.startup_id}`}
                    >
                      <Flame size={10} /> {d.interest_count_30d}
                    </div>
                  </div>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mt-0.5 flex items-center gap-1.5 flex-wrap">
                    {d.sector && <span>{d.sector}</span>}
                    {d.business_stage && <span>· {d.business_stage}</span>}
                    {d.city && (
                      <span className="inline-flex items-center gap-0.5">
                        <MapPin size={10} /> {d.city}
                      </span>
                    )}
                  </div>
                  {d.one_line_pitch && (
                    <div className="text-xs text-ink-muted mt-1 line-clamp-1">{d.one_line_pitch}</div>
                  )}
                </div>
                <ArrowRight size={12} className="text-emerald-600 flex-shrink-0 mt-1" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}
